import { t, Trans } from '@lingui/macro'
import { Delete, Edit } from '@mui/icons-material'
import AddIcon from '@mui/icons-material/Add'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Tooltip
} from '@mui/material'
import { Formik } from 'formik'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { myI18n } from 'translation/I18nConnectedProvider'
import { getEmptyLangValues } from 'utils'
import Loading from '../../../egret/components/EgretLoadable/Loading'
import {
    createActionTemplate,
    deleteActionTemplate,
    getActionTemplates
} from '../../services/sfAuth/sfData/sfActionTemplates'
import { ActionsColumnHeader } from '../common-components/ActionsColumnHeader'
import CustomDataTable from '../common-components/CustomDataTable'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { PortalTabHeader } from '../common/PortalTabHeader'
import { getTitleTranslation } from '../common/translationsCommon'
import ConfigureMultilanguageTextField from '../internal/ConfigureMultilanguageTextField'
import { muiTextLabels } from '../utilities/muiDataTablesTranslations'
import { GRANTS_PATH } from './GrantsConstants'
import PopUpDialogue from './PopUpDialogue'

export const actionsTemplatesConditions = {
  eq: (q, c) => q === c,
  gt: (q, c) => q > c,
  lt: (q, c) => q < c,
  gte: (q, c) => q >= c,
  lte: (q, c) => q <= c
}

const ActionTemplates = () => {
  const [data, setData] = useState(null)
  const history = useHistory()
  const user = useSelector((state) => state.user)

  const fetchData = () => {
    return getActionTemplates({ listPermissions: true }).then(result => {
      const toSet = []
      Object.keys(result).forEach(key => {
        const element = result[key]
        toSet.push({
          name: getTitleTranslation(
            user.language,
            element.translation
          ),
          actions: element.template.Id
        })
      })
      setData(toSet)
    })
  }

  useEffect(() => {
    fetchData()
  }, [user.language])

  if (!data) {
    return <Loading atCenter />
  }

  return (
    (
      <div className='portal-tab-container'>
        <PortalTabHeader
          header={<Trans>ACTION_TEMPLATES_HEADER</Trans>}
        />
        <CustomDataTable
          title={
            <CreateTemplate fetchData={fetchData} />
          }
          data={data}
          options={{
            textLabels: muiTextLabels(myI18n),
            filter: false,
            selectableRows: 'none',
            print: false,
            download: false,
            viewColumns: false
          }}
          columns={[
            {
              name: 'name',
              label: myI18n._(t`Action name`),
              options: {
                customHeadLabelRender: props => {
                  return (
                    <div>
                      <Trans>Action name</Trans>
                    </div>
                  )
                },
                customBodyRender: (value, tableMeta, updateValue) => {
                  return (
                    <div style={{ flex: 1 }}>{value}</div>
                  )
                }
              }
            },
            {
              name: 'actions',
              label: myI18n._(t`Actions`),
              options: {
                sort: false,
                customHeadLabelRender: props =>
                  <ActionsColumnHeader />,
                customBodyRender: (value, tableMeta, updateValue) => {
                  return (
                    (
                      <Grid
                        container
                        sx={{
                          justifyContent: {
                            xs: 'flex-start',
                            lg: 'flex-start'
                          }
                        }}
                      >
                        <Tooltip title={<Trans>ACTION_TEMPLATES_EDIT_TEMPLATE_BUTTON_TOOLTIP</Trans>}>
                          <IconButton
                            sx={{ marginRight: '12px' }}
                            onClick={() => {
                              history.push(`${GRANTS_PATH.ACTION_TEMPLATE}/${value}`)
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <DeleteTemplateButton
                          id={value}
                          fetchData={fetchData}
                        />
                      </Grid>
                    )
                  )
                }
              }
            }
          ]}
        />
      </div>
    )
  )
}

const DeleteTemplateButton = ({ id, fetchData }) => {
  const [dialogOpened, setDialogOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()

  const handleDelete = () => {
    enqueueSnackbar(<Trans>Deleting action template</Trans>, {
      variant: 'info'
    })
    setDeleting(true)
    setDialogOpen(false)
    deleteActionTemplate(id).then(
      result => {
        fetchData().then(result => {
          setDeleting(false)
          enqueueSnackbar(<Trans>Action template deleted!</Trans>, {
            variant: 'success'
          })
        })
      },
      reject => {
        setDeleting(false)
        enqueueSnackbar(
          <Trans>
            Error occured while deleting action template!
          </Trans>,
          {
            variant: 'error'
          }
        )
      }
    )
  }

  return (
    <>
      <Tooltip title={<Trans>ACTION_TEMPLATES_DELETE_TEMPLATE_BUTTON_TOOLTIP</Trans>}>
        <IconButton
          disabled={deleting}
          onClick={() => {
            setDialogOpen(true)
          }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
      <PopUpDialogue
        type='okCancel'
        cancelText={<Trans>No</Trans>}
        okText={<Trans>Yes</Trans>}
        dialogTitle={<Trans>Are you sure you want to delete this action template?</Trans>}
        open={dialogOpened}
        closeFunction={() => setDialogOpen(false)}
        cancelFunction={() => setDialogOpen(false)}
        confirmFunction={() => handleDelete()}
      />
    </>
  )
}

const CreateTemplate = ({ fetchData }) => {
  const history = useHistory()
  const [dialogOpened, setDialogOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const { enqueueSnackbar } = useSnackbar()

  return (
    <>
      <Button
        variant='contained'
        color='primary'
        onClick={e => {
          setDialogOpen(true)
        }}
        endIcon={<AddIcon sx={{ marginLeft: '5px' }} />}
      >
        <Trans>Add new action template</Trans>
      </Button>
      <Formik
        initialValues={{
          templateName: '',
          description: getEmptyLangValues(),
          name: getEmptyLangValues()
        }}
      >
        {formikState => (
          <Dialog open={dialogOpened} maxWidth='md'>
            <div>
              <DialogTitleWithIconClose
                label={<Trans>ACTION_TEMPLATES_ADD_NEW_ACTION_TEMPLATE_DIALOG_TITLE</Trans>}
                handleClose={() => {
                  setDialogOpen(false)
                }}
              />
              <DialogContent>
                <Grid
                  container
                  direction='column'
                  justifyContent='center'
                  alignItems='center'
                  sx={{ paddingTop: '10px' }}
                >
                  <ConfigureMultilanguageTextField
                    value={formikState.values.name}
                    handleChange={e => {
                      formikState.setFieldValue('name', e)
                    }}
                    inputProps={{ maxLength: 80 }}
                    label={<Trans>Name</Trans>}
                  />
                  <ConfigureMultilanguageTextField
                    value={formikState.values.description}
                    handleChange={e => {
                      formikState.setFieldValue('description', e)
                    }}
                    style={{ marginTop: 20 }}
                    rows={4}
                    multiline
                    label={<Trans>Description</Trans>}
                  />
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  variant='contained'
                  disabled={creating}
                  color='primary'
                  onClick={e => {
                    enqueueSnackbar(<Trans>Creating action template</Trans>, {
                      variant: 'info'
                    })
                    setCreating(true)
                    createActionTemplate(
                      formikState.values
                    ).then(result => {
                      history.push(`${GRANTS_PATH.ACTION_TEMPLATE}/${result.id}`)
                    })
                  }}
                  endIcon={<AddIcon sx={{ marginLeft: '5px' }} />}
                >
                  <Trans>NEW_ACTION_TEMPLATE_CREATE_AND_GO_BUTTON</Trans>
                </Button>
                <Button
                  variant='contained'
                  disabled={creating}
                  onClick={e => {
                    enqueueSnackbar(<Trans>Creating action template</Trans>, {
                      variant: 'info'
                    })
                    setCreating(true)
                    createActionTemplate(
                      formikState.values
                    ).then(result => {
                      fetchData().then(result => {
                        formikState.resetForm({})
                        setCreating(false)
                        enqueueSnackbar(
                          <Trans>Action template created!</Trans>,
                          {
                            variant: 'success'
                          }
                        )
                        setDialogOpen(false)
                      })
                    })
                  }}
                  endIcon={<AddIcon sx={{ marginLeft: '5px' }} />}
                >
                  <Trans>NEW_ACTION_TEMPLATE_CREATE_BUTTON</Trans>
                </Button>
              </DialogActions>
            </div>
          </Dialog>
        )}
      </Formik>
    </>
  )
}

export default ActionTemplates
